import { Subject } from 'rxjs';
import { filter, debounceTime } from 'rxjs/operators';
import { nameOfClass } from '@tencent/workbench-core';
import { $logger } from '../logger/logger.js';
import { ActionPointRunner, isIActionPointRunner } from '../../providers/action-point/action-point-runner.js';
import { HandlerBuilder } from '../../providers/middleware/handler-builder.js';
import { NonFunctionConfigMap } from './non-function-config-map.js';
import { CallbackConfigMap } from './callback-config-map.js';

/**
 * 工作台 UI 管理器基类
 *
 * @typeparam C - 原始配置项接口
 * @typeparam S - 配置表接口
 *
 * @public
 */
class UIService {
    constructor() {
        this.debounceTime = 10;
        this.configs = new NonFunctionConfigMap();
        this.callbacks = new CallbackConfigMap();
        this.middlewares = new Map();
        this.subject = new Subject();
        this.subject.asObservable().pipe(filter(() => {
            var _a;
            if (!this.interceptor) {
                return true;
            }
            return !!((_a = this.interceptor) === null || _a === void 0 ? void 0 : _a.call(this));
        }), debounceTime(this.debounceTime))
            .subscribe(value => this.onUpdate(value));
    }
    setUpdateInterceptor(interceptor) {
        this.interceptor = interceptor;
    }
    /**
     * 根据schema更新ui, 需要子类基于 schema 的类型自行处理
     * @internal
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onUpdate(value) {
        // 空实现，需要子类基于 schema 的类型自行处理
    }
    /**
     * 向管理器注册配置项
     * @param config - 配置
     * @param updateImmediately - 是否立刻更新
     */
    register(config, updateImmediately = false) {
        // 如果用了keyof过滤never, typescript目前就不能正确识别类型，所以暂时使用any大法
        // ts版本 4.2.3
        // https://github.com/microsoft/TypeScript/issues/38646
        if (this.hasConfig(config.name)) {
            $logger.warn(`The config "${config.name}" has already been registered, it has been replaced by new config.`);
        }
        this.configs.set(config.name, config);
        if (updateImmediately) {
            this.updateState();
        }
    }
    /**
     * 从管理器中删除某个配置
     * @param key - 配置 key
     */
    remove(key) {
        this.configs.remove(key);
    }
    /**
     * 查询管理中是否存在某配置，静态配置不存在则认为配置不存在
     * @param key - 配置 key
     */
    hasConfig(key) {
        return this.configs.has(key);
    }
    getMergedConfig(key) {
        if (this.hasConfig(key)) {
            // config为三部分组成
            // 1. ui config
            const uiConfig = this.getUIConfig(key);
            // 2. callback config
            const callbackConfig = this.callbacks.getAll(key);
            const callbacks = {};
            callbackConfig.forEach((item, name) => {
                callbacks[name] = item;
            });
            // 3. middleware config
            this.middlewares.forEach((handlers, name) => {
                if (callbacks[name]) {
                    callbacks[name] = callbacks[name].merge(handlers);
                }
                else {
                    callbacks[name] = new ActionPointRunner(handlers);
                }
            });
            const config = Object.assign(Object.assign({}, uiConfig), callbacks);
            this.updateActionPoint(config);
            return config;
        }
        return undefined;
    }
    updateActionPoint(config) {
        const matchPoint = this.callbacks.getMatchPoint(config.name);
        for (const [key, value] of Object.entries(config)) {
            if (isIActionPointRunner(value)) {
                value.point = Object.assign(Object.assign({}, value.point), { from: nameOfClass(this.constructor), name: config.name, trigger: key, matchPoint });
            }
        }
    }
    /**
     * 按名字，获取同名的方法和非方法类型配置的总和
     * @param key - 配置 key
     * @param defaultConfig - 默认 Config
     */
    get(key, defaultConfig) {
        const mergedConfig = this.getMergedConfig(key);
        if (mergedConfig) {
            return mergedConfig;
        }
        return defaultConfig;
    }
    /**
     * 按名字，获取非方法类型配置
     * @param key - 配置 key
     */
    getUIConfig(key) {
        return this.configs.get(key);
    }
    /**
     * 将静态和方法配置合成一份配置
     */
    getConfigs() {
        const config = new Map();
        this.getAllKeys().forEach((key) => {
            const mergedConfig = this.getMergedConfig(key);
            if (mergedConfig) {
                config.set(key, mergedConfig);
            }
        });
        return config;
    }
    getAllKeys() {
        return this.configs.getAllKeys();
    }
    /**
     * 重新计算schema和config并更新ui
     * @internal
     */
    updateState() {
        this.subject.next(this.getConfigs());
    }
    /**
     * 更新UI, 不重新合并计算schema和config
     * @public
     * @param updateImmediately - 是否立即更新ui
     */
    update(updateImmediately = false) {
        if (updateImmediately) {
            this.$forceUpdate();
        }
        else {
            this.subject.next(this.getConfigs());
        }
    }
    /**
     * 为一个 UI 配置绑定一个回调方法
     *
     * @param key - UI 配置 key
     * @param callbackName - 回调方法名
     * @param runner - 回调方法
     * @param updateImmediately - 是否立即更新 UI
     */
    bind(key, callbackName, runner, updateImmediately = false) {
        let callbackMap = this.callbacks.get(key);
        if (!callbackMap) {
            callbackMap = new Map();
            this.callbacks.set(key, callbackMap);
        }
        const oldRunner = callbackMap.get(callbackName);
        if (oldRunner) {
            $logger.warn(`There is already a "${String(callbackName)}" action point for config "${key}" and will be override by duplicate declaration. Search "@${oldRunner.point.from}('${key}', '${String(callbackName)}')" in your codebase to find it.`);
        }
        callbackMap.set(callbackName, runner);
        if (updateImmediately) {
            this.updateState();
        }
    }
    /**
     * 为该服务特定方法增加一个全局中间件
     *
     * @param middleware - 中间件
     * @param callbackName - 回调方法名
     */
    use(middleware, callbackName) {
        const handler = HandlerBuilder.from(middleware).build();
        let callbackQueue = this.middlewares.get(callbackName);
        if (callbackQueue) {
            callbackQueue.unshift(handler);
        }
        else {
            callbackQueue = [handler];
        }
        this.middlewares.set(callbackName, callbackQueue);
        this.update();
    }
    /**
     * 获取对应名字的所有回调
     * @param key - 配置 key
     */
    getCallbackConfig(key) {
        return this.callbacks.get(key);
    }
}

export { UIService };
//# sourceMappingURL=ui-service.js.map
